import { ChevronLeft, ChevronRight, ExternalLink, ImageOff, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useSearchParams } from "react-router";
import { apiGet } from "../api";
import type { PortalConfig } from "../config";
import { dateTime } from "../format";
import type { Lookups } from "../types";

interface Photo {
  id: string; url: string | null; takenAt: string; legacy: boolean;
  ticketNo: number; customer: string; status: string; closed: boolean; watchmaker: string | null;
  watch: { position: number; brand: string | null; model: string | null; description: string | null; condition: string | null } | null;
  ticketDescription: string | null; brands: string | null;
}

const watchName = (p: Photo) => [p.watch?.brand, p.watch?.model].filter(Boolean).join(" ") || p.brands || "";

/**
 * Image Gallery (owner, 2026-11-04): every ticket photo, newest first, mainly to find a watch or the ticket it
 * belongs to after a mix-up. Search by watch (brand, model, description), customer or ticket number; filter by the
 * date the photo was taken, open/finished or status, and watchmaker. Click a photo for a large view with its ticket.
 */
export function GalleryPage({ config, lookups }: { config: PortalConfig; lookups: Lookups | null }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [params, setParams] = useSearchParams();
  const filters = { q: params.get("q") ?? "", from: params.get("from") ?? "", to: params.get("to") ?? "", status: params.get("status") ?? "", watchmaker: params.get("watchmaker") ?? "" };
  const key = JSON.stringify(filters);
  const [text, setText] = useState(filters.q);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const run = useRef(0);

  const set = (k: keyof typeof filters, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n, { replace: true });
  };

  // Typing searches after a short pause.
  useEffect(() => {
    if (text.trim() === filters.q) return;
    const t = setTimeout(() => set("q", text.trim()), 450);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (before: string | null) => {
    if (!token) return;
    const id = ++run.current;
    setLoading(true); setError("");
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    if (before) qs.set("before", before);
    try {
      const r = await apiGet<{ photos: Photo[]; next: string | null }>(config, token, `/gallery?${qs}`);
      if (id !== run.current) return;
      setPhotos((list) => (before ? [...list, ...r.photos] : r.photos));
      setNext(r.next);
    } catch (e) { if (id === run.current) setError((e as Error).message); }
    finally { if (id === run.current) setLoading(false); }
  };
  useEffect(() => { setOpen(null); void load(null); }, [key, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const watchmakers = (lookups?.locations ?? []).filter((l) => l.kind === "watchmaker" && (l.isActive || l.id === filters.watchmaker));
  const statuses = (lookups?.statuses ?? []).filter((s) => s.isActive || String(s.id) === filters.status);
  const any = Object.values(filters).some(Boolean);

  return (
    <div className="report-page gallery-page">
      <div className="report-head"><h2>Image Gallery</h2>
        {photos.length > 0 && <span className="muted small">{photos.length}{next ? "+" : ""} photo{photos.length === 1 && !next ? "" : "s"}</span>}
      </div>
      <div className="gallery-filters">
        <label className="gallery-search"><Search size={16} />
          <input type="search" placeholder="Watch (brand, model, dial, condition), customer or ticket #" value={text} autoFocus
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") set("q", text.trim()); }} />
        </label>
        <label>From <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => set("from", e.target.value)} /></label>
        <label>To <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => set("to", e.target.value)} /></label>
        <label>Status <select value={filters.status} onChange={(e) => set("status", e.target.value)}>
          <option value="">Any</option><option value="open">Open tickets</option><option value="closed">Finished tickets</option>
          <optgroup label="Status">{statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>
        </select></label>
        <label>Watchmaker <select value={filters.watchmaker} onChange={(e) => set("watchmaker", e.target.value)}>
          <option value="">Any</option>{watchmakers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select></label>
        {any && <button type="button" className="tb-btn" onClick={() => { setText(""); setParams(new URLSearchParams(), { replace: true }); }}>Clear</button>}
      </div>
      {error && <p className="error">{error}</p>}
      {!loading && !error && !photos.length && <p className="muted">{any ? "No photos match. Try fewer words or a wider date range." : "No photos yet."}</p>}

      <ul className="gallery-grid">
        {photos.map((p, i) => (
          <li key={p.id}>
            <button type="button" className="g-tile" onClick={() => setOpen(i)} aria-label={`Photo from ticket ${p.ticketNo}`}>
              <Thumb url={p.url} />
              <span className="g-cap">
                <strong>#{p.ticketNo}</strong> {p.customer}
                {watchName(p) && <span className="g-watch">{watchName(p)}</span>}
                <span className="g-meta">{new Date(p.takenAt).toLocaleDateString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", year: "numeric" })} · {p.status}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {loading && <p className="muted">Loading…</p>}
      {next && !loading && <button type="button" className="tb-btn g-more" onClick={() => void load(next)}>Load more</button>}

      {open != null && photos[open] && (
        <Lightbox photo={photos[open]} index={open} count={photos.length} hasMore={!!next}
          onClose={() => setOpen(null)}
          onMove={(d) => {
            const n = open + d;
            if (n >= 0 && n < photos.length) setOpen(n);
            else if (n === photos.length && next && !loading) void load(next).then(() => setOpen(n));
          }} />
      )}
    </div>
  );
}

function Thumb({ url }: { url: string | null }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) return <span className="g-img g-missing"><ImageOff size={22} /><small>Photo not available</small></span>;
  return <img className="g-img" src={url} alt="" loading="lazy" onError={() => setBroken(true)} />;
}

function Lightbox({ photo: p, index, count, hasMore, onClose, onMove }: {
  photo: Photo; index: number; count: number; hasMore: boolean; onClose: () => void; onMove: (d: number) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { const d = ref.current; if (d && !d.open) d.showModal(); return () => d?.close(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "ArrowLeft") onMove(-1); if (e.key === "ArrowRight") onMove(1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onMove]);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [p.id]);
  const w = p.watch;
  return (
    <dialog ref={ref} className="lightbox" aria-label={`Photo from ticket ${p.ticketNo}`} onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClick={(e) => { if (e.target === ref.current) onClose(); }}>
      <div className="lb-img">
        {p.url && !broken ? <img src={p.url} alt="" onError={() => setBroken(true)} /> : <span className="g-missing"><ImageOff size={32} /><small>Photo not available</small></span>}
        <button type="button" className="lb-nav prev" aria-label="Previous photo" disabled={index === 0} onClick={() => onMove(-1)}><ChevronLeft size={28} /></button>
        <button type="button" className="lb-nav next" aria-label="Next photo" disabled={index === count - 1 && !hasMore} onClick={() => onMove(1)}><ChevronRight size={28} /></button>
      </div>
      <aside className="lb-info">
        <header><h3><Link to={`/tickets/${p.ticketNo}`}>Ticket #{p.ticketNo}</Link></h3>
          <button type="button" className="icon-link" aria-label="Close" onClick={onClose}><X size={20} /></button></header>
        <dl>
          <dt>Customer</dt><dd>{p.customer || "—"}</dd>
          <dt>Status</dt><dd>{p.status}</dd>
          {p.watchmaker && <><dt>Watchmaker</dt><dd>{p.watchmaker}</dd></>}
          {w && <><dt>Watch {w.position}</dt><dd>{[w.brand, w.model].filter(Boolean).join(" ") || "—"}{w.description && <><br />{w.description}</>}{w.condition && <><br /><span className="muted">{w.condition}</span></>}</dd></>}
          {!w && (p.brands || p.ticketDescription) && <><dt>Watch</dt><dd>{[p.brands, p.ticketDescription].filter(Boolean).join(" · ")}</dd></>}
          <dt>Photo taken</dt><dd>{dateTime(p.takenAt)}{p.legacy ? " (old system)" : ""}</dd>
        </dl>
        <div className="lb-actions">
          <Link className="tb-btn primary" to={`/tickets/${p.ticketNo}`}>Open ticket</Link>
          {p.url && <a className="tb-btn" href={p.url} target="_blank" rel="noreferrer"><ExternalLink size={15} />Full size</a>}
        </div>
        <p className="muted small">{index + 1} of {count}{hasMore ? "+" : ""} · use ← → to move</p>
      </aside>
    </dialog>
  );
}
